import type { FC } from 'react';
import { useCallback, useContext, useEffect, useState, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { BiData, BuyCoreButtonBiEvent } from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { PapiDeviation } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import MultiselectField from '@wix/da-ds/pkg/Dropdown/multiselect/MultiselectField';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import DesktopBatchActionWrapper from '../DesktopBatchActionWrapper';
import MobileBatchActionWrapper from '../MobileBatchActionWrapper';
import { BatchActionProps } from '../types';
import CreateTierCallToAction from './CreateTierCallToAction';

export interface Props extends BatchActionProps {
  tiers?: PapiDeviation[];
  isLoading?: boolean;
  batchLoadTiers: () => void;
  batchEditTier: (tierIds: number[]) => void;
}

const TierBatchEdit: FC<Props> = ({
  selectionArea,
  tiers,
  isLoading,
  batchLoadTiers,
  batchEditTier,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { hasCoreMembership } = useContext(SessionContext);
  const inputId = useId();

  const mapTierToValue = useCallback(
    (tier: PapiDeviation) => ({
      value: `${tier.deviationId}`,
      label: tier.title,
      selected: false,
    }),
    []
  );

  const [value, setValue] = useState((tiers || []).map(mapTierToValue));

  useEffect(() => {
    if (!tiers && !isLoading) {
      batchLoadTiers();
    }
  }, [tiers, isLoading, batchLoadTiers]);

  useEffect(() => {
    if (tiers) {
      setValue(tiers.map(mapTierToValue));
    }
  }, [tiers, mapTierToValue]);

  const handleSave = useCallback(() => {
    const selectedTierIds = value
      .filter(item => item.selected)
      .map(item => parseInt(item.value, 10));

    batchEditTier(selectedTierIds);
  }, [batchEditTier, value]);

  const handleChange = useCallback(
    (isSelected, itemId) => {
      setValue(
        value.map(item => ({
          ...item,
          selected: item.value === itemId ? isSelected : item.selected,
        }))
      );
    },
    [value]
  );

  const form = isLoading ? (
    <LoadingIndicator view="llama" />
  ) : (
    <div>
      <MultiselectField
        id={inputId}
        label={t('studio.batchActions.form.edit_tier.placeholder')}
        placeholder={t('studio.batchActions.form.edit_tier.placeholder')}
        items={value}
        onChange={handleChange}
        isLabelHidden
        withFloatingList
      />
    </div>
  );

  const coreUpgradeBiData = BiData<BuyCoreButtonBiEvent>({
    evid: 375,
    referral_info: ReferralType.STUDIO_BATCH_TIER,
  });

  if (isMobile && ((Array.isArray(tiers) && tiers.length === 0) || !hasCoreMembership)) {
    return <CreateTierCallToAction />;
  }

  if (isMobile) {
    return (
      <MobileBatchActionWrapper
        heading={t('studio.batchActions.subtitle.edit_tier')}
        onSave={handleSave}
        coreUpgradeBiData={coreUpgradeBiData}
        restrictSave="core"
      >
        {form}
      </MobileBatchActionWrapper>
    );
  }
  return (
    <DesktopBatchActionWrapper
      heading={t('studio.batchActions.subtitle.edit_tier')}
      selectionArea={selectionArea}
      onSave={handleSave}
      coreUpgradeBiData={coreUpgradeBiData}
      restrictSave="core"
    >
      {form}
    </DesktopBatchActionWrapper>
  );
};
TierBatchEdit.displayName = 'TierBatchEdit';

export default TierBatchEdit;
